/**
 * Hook for handling Stripe Radar fraud detection
 */
import { useCallback, useRef } from 'react';
export function useStripeRadarAction({ paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef, }) {
    const scriptLoadingRef = useRef(false);
    const handleStripeRadar = useCallback(async (payment, actionData, options = {}, handlePaymentAction) => {
        const radarConfig = actionData.metadata?.radar;
        if (!radarConfig?.publishableKey) {
            console.error('Stripe radar config missing from payment action');
            setError('Missing Stripe Radar configuration');
            setIsLoading(false);
            options.onFailure?.('Missing Stripe Radar configuration');
            options.onPaymentFailed?.({
                code: 'STRIPE_RADAR_MISSING_CONFIG',
                message: 'Missing Stripe Radar configuration',
                payment,
            });
            return;
        }
        try {
            // Load Stripe if not already loaded
            if (typeof window !== 'undefined' && typeof window.Stripe !== 'function' && !scriptLoadingRef.current) {
                console.log('Loading Stripe script...');
                scriptLoadingRef.current = true;
                // Check if script already exists
                const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
                if (existingScript) {
                    console.log('Stripe script already exists, waiting for load...');
                    if (typeof window.Stripe === 'function') {
                        console.log('Stripe already loaded');
                    }
                    else {
                        // Wait for existing script to load
                        await new Promise((resolve, reject) => {
                            const checkStripe = () => {
                                if (typeof window.Stripe === 'function') {
                                    resolve();
                                }
                                else {
                                    setTimeout(checkStripe, 100);
                                }
                            };
                            checkStripe();
                            setTimeout(() => reject(new Error('Timeout waiting for Stripe')), 10000);
                        });
                    }
                }
                else {
                    const script = document.createElement('script');
                    script.src = 'https://js.stripe.com/v3/';
                    script.async = true;
                    document.head.appendChild(script);
                    await new Promise((resolve, reject) => {
                        script.onload = () => {
                            console.log('Stripe script loaded successfully');
                            scriptLoadingRef.current = false;
                            resolve();
                        };
                        script.onerror = () => {
                            scriptLoadingRef.current = false;
                            reject(new Error('Failed to load Stripe'));
                        };
                    });
                }
            }
            console.log('Initializing Stripe with public key:', radarConfig.publishableKey);
            const stripe = window.Stripe(radarConfig.publishableKey);
            const result = await stripe.createRadarSession();
            console.log('Stripe Radar result:', result);
            if (result.error) {
                const errorMessage = result.error.message || 'Failed to create Radar session';
                console.error('Stripe Radar error:', errorMessage);
                throw new Error(errorMessage);
            }
            if (!result.radarSession) {
                const errorMessage = 'No radar session returned from Stripe';
                console.error(errorMessage);
                throw new Error(errorMessage);
            }
            console.log('Stripe Radar session created:', result.radarSession);
            // Save radar session to database
            await paymentsResource.saveRadarSession({
                orderId: radarConfig.orderId,
                stripeRadarSessionId: result.radarSession.id,
                stripeRadarSessionData: result.radarSession,
            });
            console.log('Radar session saved to database');
            // Resume payment by calling completePaymentAfterAction
            const resumedPayment = await paymentsResource.completePaymentAfterAction(payment.id);
            console.log('Payment resumed after Stripe radar:', resumedPayment);
            // Handle the resumed payment response
            if (resumedPayment.status === 'declined' || resumedPayment.status === 'failed') {
                // Payment declined or failed - extract error message from response
                const errorMsg = resumedPayment.error?.message
                    || resumedPayment.error?.processorMessage
                    || 'Payment declined';
                console.error('❌ [usePayment] Payment declined after Stripe radar:', errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: resumedPayment.error?.code || 'PAYMENT_DECLINED',
                    message: errorMsg,
                    payment: resumedPayment,
                });
                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentFailed) {
                    hookOptionsRef.current.onPaymentFailed(errorMsg, {
                        isRedirectReturn: false,
                    });
                }
            }
            else if (resumedPayment.status === 'succeeded') {
                // Payment succeeded
                setIsLoading(false);
                const response = {
                    paymentId: resumedPayment.id,
                    payment: resumedPayment,
                    order: resumedPayment.order,
                };
                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentCompleted) {
                    await hookOptionsRef.current.onPaymentCompleted(resumedPayment, {
                        isRedirectReturn: false,
                        order: response.order,
                    });
                }
                options.onSuccess?.(response);
                options.onPaymentSuccess?.(response);
            }
            else if (resumedPayment.requireAction !== 'none' && resumedPayment.requireActionData) {
                // Payment requires another action (e.g., 3DS)
                await handlePaymentAction(resumedPayment, options);
            }
            else {
                // Start polling for final status
                startPolling(resumedPayment.id, {
                    onRequireAction: (updatedPayment) => {
                        void handlePaymentAction(updatedPayment, options);
                    },
                    onSuccess: async (successPayment) => {
                        setIsLoading(false);
                        const response = {
                            paymentId: successPayment.id,
                            payment: successPayment,
                            order: successPayment.order,
                        };
                        // Hook-level callback (universal handler)
                        if (hookOptionsRef.current?.onPaymentCompleted) {
                            await hookOptionsRef.current.onPaymentCompleted(successPayment, {
                                isRedirectReturn: false,
                                order: response.order,
                            });
                        }
                        options.onSuccess?.(response);
                        options.onPaymentSuccess?.(response);
                    },
                    onFailure: async (errorMsg) => {
                        setError(errorMsg);
                        setIsLoading(false);
                        // Hook-level callback (universal handler)
                        if (hookOptionsRef.current?.onPaymentFailed) {
                            await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                isRedirectReturn: false,
                            });
                        }
                        options.onFailure?.(errorMsg);
                        options.onPaymentFailed?.({
                            code: 'PAYMENT_FAILED',
                            message: errorMsg,
                            payment,
                        });
                    },
                });
            }
        }
        catch (radarError) {
            const errorMsg = radarError instanceof Error ? radarError.message : 'Stripe Radar failed';
            console.error('Stripe radar error:', radarError);
            setError(errorMsg);
            setIsLoading(false);
            options.onFailure?.(errorMsg);
            options.onPaymentFailed?.({
                code: 'STRIPE_RADAR_FAILED',
                message: errorMsg,
                payment,
            });
        }
    }, [paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef]);
    return { handleStripeRadar };
}
